import { FiAlertTriangle, FiAlertCircle, FiCheckCircle, FiInfo, FiBell, FiX } from 'react-icons/fi';
import { getIcon } from '../../utils/iconMap';
import React, { useState, useEffect, useRef } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { supabase } from '../../supabase';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';


const NotificationsMenu = ({ activeWorkspace, isOpen, onClose }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  // No internal ref needed for click outside if parent handles it,
  // but keeping it doesn't hurt if we want double protection.
  // However, if parent unmounts it when !isOpen, the ref is gone.
  // The parent (Header) now handles conditional rendering via isOpen prop (inside component return null)

  useEffect(() => {
    if (activeWorkspace && isOpen) {
      fetchNotifications();
    }
  }, [activeWorkspace, isOpen]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('workspace_id', activeWorkspace.id)
        .not('link', 'ilike', '/gnomes/%') // Exclude gnome chat/insight notifications
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      setNotifications(data || []);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
        const { error } = await supabase
            .from('notifications')
            .update({ is_read: true })
            .eq('id', id);

        if (error) throw error;

        // Optimistic update
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    } catch (err) {
        console.error('Error marking notification as read:', err);
    }
  };

  const markAllAsRead = async () => {
      try {
          // Note: RLS policies in 20250618000000_ensure_notifications_table.sql
          // already restrict updates to user's own notifications or general workspace notifications.
          const { error } = await supabase
            .from('notifications')
            .update({ is_read: true })
            .eq('workspace_id', activeWorkspace.id)
            .eq('is_read', false);

          if (error) throw error;

          setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      } catch (err) {
          console.error('Error marking all as read:', err);
      }
  };

  const getIcon = (type) => {
      switch(type) {
          case 'success': return <SafeIcon icon={FiCheckCircle} className="text-green-500" />;
          case 'warning': return <SafeIcon icon={FiAlertTriangle} className="text-green-500" />;
          case 'error': return <SafeIcon icon={FiAlertCircle} className="text-red-500" />;
          default: return <SafeIcon icon={FiInfo} className="text-blue-500" />;
      }
  };

  const handleNotificationClick = (notification) => {
      markAsRead(notification.id);
      onClose();

      if (notification.link) {
          if (notification.link.startsWith('http')) {
              window.open(notification.link, '_blank');
          } else {
              navigate(notification.link);
          }
      }
  };

  if (!isOpen) return null;

  return (
    <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-xl shadow-xl border border-charcoal-100 z-50 overflow-hidden animate-fade-in">
      <div className="p-4 border-b border-charcoal-100 flex justify-between items-center">
        <h3 className="font-semibold text-charcoal-900">Notifications</h3>
        <div className="flex items-center space-x-3">
          <button
            onClick={markAllAsRead}
            className="text-xs text-blue-600 hover:text-blue-700 font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded px-1"
            aria-label="Mark all notifications as read"
          >
            Mark all as read
          </button>
          <button
            onClick={onClose}
            className="text-charcoal-400 hover:text-charcoal-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-charcoal-300 rounded"
            aria-label="Close notifications menu"
          >
            <SafeIcon icon={FiX} />
          </button>
        </div>
      </div>

      <div className="max-h-96 overflow-y-auto" role="list">
          {loading && notifications.length === 0 && (
              <div className="p-4 text-center text-charcoal-500 text-sm" role="status" aria-live="polite">Loading...</div>
          )}

          {!loading && notifications.length === 0 && (
              <div className="p-8 text-center text-charcoal-500 flex flex-col items-center justify-center" role="status" aria-live="polite">
                  <div className="w-12 h-12 bg-charcoal-50 rounded-full flex items-center justify-center mb-3">
                    <SafeIcon icon={FiBell} className="text-charcoal-400 text-xl" />
                  </div>
                  <p className="text-sm font-medium text-charcoal-900">You're all caught up! 🎉</p>
                  <p className="text-xs text-charcoal-500 mt-1">No new notifications at the moment.</p>
              </div>
          )}

          {notifications.map(notification => (
              <div
                key={notification.id}
                role="listitem"
                onClick={() => handleNotificationClick(notification)}
                className={`p-4 border-b border-charcoal-50 hover:bg-charcoal-50 transition-colors cursor-pointer group ${!notification.is_read ? 'bg-blue-50/30' : ''}`}
              >
                  <div className="flex items-start space-x-3">
                      <div className="mt-1 flex-shrink-0" aria-hidden="true">
                          {getIcon(notification.type)}
                      </div>

                      {/* Interactive content wrapper for keyboard access */}
                      <button
                        className="flex-1 min-w-0 text-left focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded px-1 -mx-1"
                        onClick={(e) => {
                            e.stopPropagation(); // Prevent parent div click
                            handleNotificationClick(notification);
                        }}
                      >
                          <span className={`block text-sm text-charcoal-900 ${!notification.is_read ? 'font-semibold' : ''}`}>
                              {notification.title}
                          </span>
                          <span className="block text-sm text-charcoal-600 mt-0.5 leading-snug">{notification.message}</span>
                          <span className="block text-xs text-charcoal-400 mt-1">
                              {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                          </span>
                      </button>

                      {!notification.is_read && (
                          <button
                            onClick={(e) => {
                                e.stopPropagation();
                                markAsRead(notification.id);
                            }}
                            className="text-xs text-blue-600 hover:text-blue-800 flex-shrink-0 p-1 rounded-full hover:bg-blue-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                            title="Mark as read"
                            aria-label={`Mark "${notification.title}" as read`}
                          >
                              <div className="w-2 h-2 bg-blue-600 rounded-full" aria-hidden="true"></div>
                          </button>
                      )}
                  </div>
              </div>
          ))}
      </div>
    </div>
  );
};

export default NotificationsMenu;
